<template>
  <!-- Left column: Calendar + Articles (desktop) -->
  <v-col cols="12" md="5" xl="5" class="landing-col pl-0">
    <!-- Calendar section -->
    <div class="ios-section">
      <div class="ios-section-header">
        <span class="ios-section-title">{{ calendarTitle || $t('myCalendar') }}</span>
      </div>
      <div class="section-content">
        <OverviewCalendar @monthChange="onMonthChange" />
      </div>
    </div>

  </v-col>

  <!-- Right column: Separate sections -->
  <v-col cols="12" md="7" xl="7" class="landing-col pr-0">

    <!-- Připnuté události -->
    <div v-if="pinnedSection.events.length" class="ios-section">
      <div class="ios-section-header">
        <div class="ios-league-title">
          <span class="ios-section-title">{{ $t('pinnedEvents', 'Připnuté události') }}</span>
        </div>
      </div>
      <NextEvents
        :events="pinnedSection.events"
        :pinnedActions="true"
        :showLeagueLogo="true"
        :showTeamLogos="true"
        :showLeagueHeader="true"
        @unpinEvent="handleUnpinEvent"
        @cancelEvent="handleCancelEvent"
        @deleteEvent="handleDeleteEvent"
        @editEvent="handleEditEvent"
      />
      <div v-if="pinnedSection.hasMore" style="padding: 0 16px;">
        <button class="load-more-btn" :disabled="pinnedSection.loading" @click="loadMore('pinned')">
          <v-progress-circular v-if="pinnedSection.loading" indeterminate size="16" width="2" />
          <template v-else>{{ $t('showMore', 'Zobrazit více') }} <span class="load-more-count">({{ pinnedSection.remaining }})</span></template>
        </button>
      </div>
    </div>

    <!-- Moje účasti -->
    <div v-if="attendanceSection.events.length" class="ios-section">
      <div class="ios-section-header">
        <div class="ios-league-title">
          <span class="ios-section-title">{{ $t('myAttendance', 'Moje účasti') }}</span>
        </div>
      </div>
      <NextEvents
        :events="attendanceSection.events"
        :showLeagueLogo="false"
        :showTeamLogos="true"
        :showLeagueHeader="true"
        @deleteEvent="handleDeleteEvent"
        @editEvent="handleEditEvent"
      />
      <div v-if="attendanceSection.hasMore" style="padding: 0 16px;">
        <button class="load-more-btn" :disabled="attendanceSection.loading" @click="loadMore('attendance')">
          <v-progress-circular v-if="attendanceSection.loading" indeterminate size="16" width="2" />
          <template v-else>{{ $t('showMore', 'Zobrazit více') }} <span class="load-more-count">({{ attendanceSection.remaining }})</span></template>
        </button>
      </div>
    </div>

    <!-- Moje týmové akce -->
    <div v-if="teamEventsSection.events.length" class="ios-section">
      <div class="ios-section-header">
        <div class="ios-league-title">
          <span class="ios-section-title">{{ $t('myTeamEvents', 'Moje týmové akce') }}</span>
        </div>
      </div>
      <NextEvents
        :events="teamEventsSection.events"
        :showLeagueLogo="false"
        :showTeamLogos="true"
        :showLeagueHeader="true"
        @deleteEvent="handleDeleteEvent"
        @editEvent="handleEditEvent"
      />
      <div v-if="teamEventsSection.hasMore" style="padding: 0 16px;">
        <button class="load-more-btn" :disabled="teamEventsSection.loading" @click="loadMore('teamEvents')">
          <v-progress-circular v-if="teamEventsSection.loading" indeterminate size="16" width="2" />
          <template v-else>{{ $t('showMore', 'Zobrazit více') }} <span class="load-more-count">({{ teamEventsSection.remaining }})</span></template>
        </button>
      </div>
    </div>

    <!-- Sledované ligy -->
    <div
      v-for="block in watchedLeagueBlocks"
      :key="block.league._id"
      class="ios-section"
    >
      <template v-if="block.events.length">
        <div
          class="ios-section-header"
          :class="{ 'cursor-pointer': block.league.type === 'league' }"
          @click="routeToLeague(block.league)"
        >
          <div class="ios-league-title">
            <img
              v-if="block.league.imageUrl"
              :src="`${UPLOADS}/leagues/h200-${block.league.imageUrl}`"
              alt=""
              class="ios-league-logo"
            />
            <span class="ios-section-title">{{ block.league.title }}</span>
            <v-icon
              v-if="block.league.type === 'league'"
              size="20"
              class="ios-league-chevron"
            >mdi-chevron-right</v-icon>
          </div>
        </div>
        <NextEvents
          :events="block.events"
          :showLeagueLogo="false"
          :showTeamLogos="true"
          :showLeagueHeader="false"
          @deleteEvent="handleDeleteEvent"
          @editEvent="handleEditEvent"
        />
        <div v-if="block.hasMore" style="padding: 0 16px;">
          <button class="load-more-btn" :disabled="block.loading" @click="loadMoreLeague(String(block.league._id))">
            <v-progress-circular v-if="block.loading" indeterminate size="16" width="2" />
            <template v-else>{{ $t('showMore', 'Zobrazit více') }} <span class="load-more-count">({{ Math.max(0, block.total - block.events.length) }})</span></template>
          </button>
        </div>
      </template>
    </div>

  </v-col>

  <EventFormModal
    v-model="showAddEventDialog"
    :eventToEdit="editedEvent"
    :initialDate="selectedDate"
    @saved="handleNewEvent"
  />

  <EventDeleteWarningModal
    v-model:show="showDeleteDialog"
    :event="eventToDelete"
    @confirm-delete="handleDelete"
  />
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, watchEffect, watch, computed } from 'vue'
import axiosInstance from '@/services/axiosInstance'
import OverviewCalendar from '@/components/OverviewCalendar.vue'
import NextEvents from '@/components/LandingComponents/NextEvents.vue'
import { IEvent, ILeague } from '@/types'
import { useAppStore } from '@/stores/app/appStore'
import { canAddEvents } from '@/utils/userUtils'
import EventFormModal from '@/components/EventFormModal.vue'
import EventDeleteWarningModal from '@/components/EventDeleteWarningModal.vue'
import { useUserStore } from '@/stores/user/userStore'
import { useRouter } from 'vue-router'

const userStore = useUserStore();
const router = useRouter();

const UPLOADS = import.meta.env.VITE_UPLOADS || 'http://localhost:3000/uploads'

const appStore = useAppStore()
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'


const showAddEventDialog = ref(false)
const selectedDate = ref<Date | null>(null);
const canAddEventAllowed = ref(false);
const calendarTitle = ref<string>('')
const editedEvent = ref<IEvent | null>(null)

// Delete dialog
const showDeleteDialog = ref(false)
const eventToDelete = ref<IEvent | null>(null)

const onMonthChange = (title: string) => {
  calendarTitle.value = title
}

// === Sekce s pagingem ===
interface PaginatedSection {
  events: IEvent[]
  total: number
  hasMore: boolean
  page: number
  limit: number
  loading: boolean
  remaining: number
}

function createSection(limit = 5): PaginatedSection {
  return reactive({
    events: [],
    total: 0,
    hasMore: false,
    page: 1,
    limit,
    loading: false,
    remaining: 0,
  })
}

const pinnedSection = createSection(20)
const attendanceSection = createSection(5)
const teamEventsSection = createSection(5)
const watchedLeagueBlocks = ref<{ league: ILeague; events: IEvent[]; total: number; hasMore: boolean; page: number; loading: boolean }[]>([])

// === Fetch functions ===

async function fetchSection(
  section: PaginatedSection,
  endpoint: string,
  page = 1,
  append = false,
) {
  section.loading = true
  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/events/index/${endpoint}`, {
      params: { page, limit: section.limit },
    })
    if (append) {
      section.events.push(...data.events)
    } else {
      section.events = data.events
    }
    section.total = data.total
    section.hasMore = data.hasMore
    section.page = data.page
    section.remaining = Math.max(0, data.total - section.events.length)
  } catch (error) {
    console.error(`Chyba při načítání ${endpoint}:`, error)
  } finally {
    section.loading = false
  }
}

async function fetchWatchedLeagues() {
  try {
    const { data } = await axiosInstance.get<{ league: ILeague; events: IEvent[]; total: number; hasMore: boolean }[]>(
      `${API_BASE_URL}/events/index/watched-leagues`
    )
    watchedLeagueBlocks.value = data.map(block => ({
      ...block,
      page: 1,
      loading: false,
    }))
  } catch (error) {
    console.error('Chyba při načítání sledovaných lig:', error)
  }
}

async function loadMoreLeague(leagueId: string) {
  const block = watchedLeagueBlocks.value.find(b => String(b.league._id) === leagueId)
  if (!block || block.loading) return

  block.loading = true
  try {
    const nextPage = block.page + 1
    const { data } = await axiosInstance.get(`${API_BASE_URL}/events/index/league/${leagueId}`, {
      params: { page: nextPage, limit: 5 },
    })
    block.events.push(...data.events)
    block.total = data.total
    block.hasMore = data.hasMore
    block.page = nextPage
  } catch (error) {
    console.error('Chyba při načítání dalších událostí ligy:', error)
  } finally {
    block.loading = false
  }
}

async function fetchAllSections() {
  await Promise.all([
    fetchSection(pinnedSection, 'pinned', 1),
    fetchSection(attendanceSection, 'attendance', 1),
    fetchSection(teamEventsSection, 'team-events', 1),
    fetchWatchedLeagues(),
  ])
}

async function loadMore(sectionKey: 'pinned' | 'attendance' | 'teamEvents') {
  const sectionMap = {
    pinned: { section: pinnedSection, endpoint: 'pinned' },
    attendance: { section: attendanceSection, endpoint: 'attendance' },
    teamEvents: { section: teamEventsSection, endpoint: 'team-events' },
  }
  const { section, endpoint } = sectionMap[sectionKey]
  await fetchSection(section, endpoint, section.page + 1, true)
}

// Funkce pro přidání nové události
const handleNewEvent = async () => {
  editedEvent.value = null
  appStore.triggerRefetch();
};

// Delete event handler
function handleDeleteEvent(event: IEvent) {
  eventToDelete.value = event
  if (event._id) showDeleteDialog.value = true
}

function handleCancelEvent(event: IEvent) {
  handleDeleteEvent(event)
}

async function handleUnpinEvent(event: IEvent) {
  if (!event._id) return
  try {
    await axiosInstance.put(`/events/${event._id}`, { pin: false })
    appStore.triggerRefetch()
  } catch (e) {
    console.error('Chyba při odepínání události:', e)
  }
}

async function handleDelete() {
  if (!eventToDelete.value?._id) return
  try {
    await axiosInstance.delete(`/events/${eventToDelete.value._id}`)
    appStore.triggerRefetch()
  } catch (e) {
    console.error('Chyba při mazání události:', e)
  }
}

// Edit event handler
async function handleEditEvent(event: IEvent) {
  try {
    const { data } = await axiosInstance.get(`/events/${event._id}`)
    editedEvent.value = data
  } catch (e) {
    console.error('Chyba při načítání události:', e)
    editedEvent.value = { ...event }
  }
  showAddEventDialog.value = true
}

const routeToLeague = (league: any) => {
  if(league.type === 'league'){
    router.push(`/leagues/${league.urlTitle}/${league.activeSeason?.year}/standings`);
  } else {
    //router.push(`/tournaments/${league.urlTitle}/overview`);
  }
}

watchEffect(async () => {
  if (appStore.needRefetch) {
    await fetchAllSections()
    appStore.clearRefetchFlag()
  }
})

onMounted(async () => {
  const res = await canAddEvents();
  canAddEventAllowed.value = res.allowed;
  await fetchAllSections();
})

const userId = computed(() => userStore.getUserId);

watch(userId, async (newVal) => {
  const res = await canAddEvents();
  canAddEventAllowed.value = res.allowed;
});

</script>

<style lang="scss" scoped>
.landing-col {
  padding-top: 0;
  padding-bottom: 0;

  @media (max-width: 959px) {
    padding: 0 !important;
  }
}

.ios-section {
  margin-bottom: 24px;
}



.ios-header-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgb(var(--v-theme-sheetText));
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.9);
    background: rgba(255, 255, 255, 0.25);
  }
}

.ios-header-link {
  font-size: 14px;
  font-weight: 500;
  color: rgb(var(--v-theme-sheetTextSecondary));
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px 0;
  transition: opacity 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &:active {
    opacity: 0.6;
  }
}

.ios-league-title {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.ios-league-logo {
  height: 32px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
}

.ios-league-chevron {
  color: rgba(var(--v-theme-sheetTextSecondary), 0.6);
  flex-shrink: 0;
}

</style>

<style lang="scss">
/* Calendar section-content child overrides (unscoped to avoid specificity issues with global styles) */
.section-content {
  color: rgb(var(--v-theme-sheetText));

  .legend {
    background-color: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    color: rgb(var(--v-theme-sheetText)) !important;
  }

  .v-card {
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    color: rgb(var(--v-theme-sheetText)) !important;
  }

  .blur-white {
    background-color: transparent !important;
    border: none !important;
    border-radius: 0 !important;
    color: rgb(var(--v-theme-sheetText)) !important;
  }

  .next-events-card {
    background: transparent !important;
    border: none !important;
    border-radius: 0 !important;
  }

  .my-event-tile {
    color: rgb(var(--v-theme-sheetText)) !important;
  }

  .dummy-tile {
    color: rgb(var(--v-theme-sheetTextSecondary)) !important;
  }

  .compact-date,
  .match-tile-desc {
    color: rgba(var(--v-theme-sheetText), 0.5) !important;
  }

  .ios-row-dot {
    color: rgba(var(--v-theme-sheetText), 0.5) !important;
  }

  .mhred {
    color: #d32f2f !important;
  }

  .v-icon {
    color: inherit;
  }
}
</style>
